/* ============================================================
   MONNAIE DU MONDE — entete.js
   Comportement de l'en-tête (section header + blocs entete-*) :
     - méga-menus : survol avec délais (ou clic), clavier (flèches, Échap, Tab),
       aria-expanded, fermeture au clic extérieur / sur un lien, mode tactile,
       hauteur maximale des panneaux (variable --entete-bas) ;
     - menu Monde : continents → panneau des pays (survol, focus, clic, flèches) ;
     - tiroir mobile : ouverture / fermeture, Échap, piège de focus, blocage du défilement ;
     - recherche mobile : la loupe déplie le champ sous l'en-tête ;
     - libellé accessible du panier tenu à jour avec le badge.
   Idempotent, compatible éditeur de thème (shopify:section:load, shopify:block:select).
   ============================================================ */
(function () {
  'use strict';

  var OUVERT = 'ouvert';
  var MQ_TIROIR = '(max-width: 1179px)';
  var etat = { clavier: false, pointeur: 'mouse', global: false };

  function tous(sel, racine) {
    return Array.prototype.slice.call((racine || document).querySelectorAll(sel));
  }
  function visible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }
  function focusables(racine) {
    return tous('a[href], button:not([disabled]), summary, input, [tabindex]:not([tabindex="-1"])', racine)
      .filter(function (el) { return visible(el) && !el.closest('[hidden]'); });
  }

  /* ---------- hauteur disponible sous l'en-tête (panneaux de méga-menu) ---------- */
  function majBas(header) {
    if (!header) return;
    var r = header.getBoundingClientRect();
    header.style.setProperty('--entete-bas', Math.max(0, Math.round(r.bottom)) + 'px');
  }

  /* L'espace libéré compense la réduction du sticky : aucun saut du contenu. */
  function initCompact(header) {
    if (header._compactEntete) return;
    var cadre = header.closest('.shopify-section') || header.parentElement;
    var espace = document.createElement('div');
    espace.setAttribute('aria-hidden', 'true');
    espace.className = 'entete-espace';
    cadre.insertAdjacentElement('afterend', espace);
    var reduit = false, hauteurNormale = header.offsetHeight, raf = 0;
    var bureau = window.matchMedia('(min-width:1025px)');

    function mesurer() {
      if (!reduit) hauteurNormale = header.offsetHeight;
      espace.style.height = (reduit ? Math.max(0, hauteurNormale - header.offsetHeight) : 0) + 'px';
      var collant = getComputedStyle(cadre).position === 'sticky';
      document.documentElement.style.setProperty('--hauteur-entete', (collant ? header.offsetHeight : 0) + 'px');
      majBas(header);
    }
    function actualiser() {
      raf = 0;
      var autorise = header.dataset.compactScroll === 'true' && (bureau.matches || header.dataset.stickyMobile !== 'false');
      var souhaite = autorise && (reduit ? window.scrollY > 16 : window.scrollY > Math.max(112, hauteurNormale + 20));
      /* Ne pas déplacer ou cacher le champ de recherche pendant sa saisie. */
      var saisie = header.contains(document.activeElement) && /^(INPUT|SELECT|TEXTAREA)$/.test(document.activeElement.tagName);
      if (souhaite !== reduit && !saisie) reduit = souhaite;
      header.classList.toggle('header--reduit', reduit);
      header.classList.toggle('header--compact', header.dataset.disposition === 'compact');
      mesurer();
    }
    function planifier() { if (!raf) raf = requestAnimationFrame(actualiser); }
    window.addEventListener('scroll', planifier, { passive: true });
    window.addEventListener('resize', planifier);
    header.addEventListener('focusout', planifier);
    var observation = window.ResizeObserver ? new ResizeObserver(mesurer) : null;
    if (observation) observation.observe(header);
    header._compactEntete = function () {
      window.removeEventListener('scroll', planifier);
      window.removeEventListener('resize', planifier);
      header.removeEventListener('focusout', planifier);
      if (observation) observation.disconnect();
      if (raf) cancelAnimationFrame(raf);
      espace.remove();
    };
    actualiser();
  }

  /* ============================================================
     MÉGA-MENUS
     ============================================================ */
  function initNav(header) {
    var nav = header.querySelector('[data-menu-principal]');
    if (!nav || nav.dataset.lieNav) return;
    nav.dataset.lieNav = '1';

    var entrees = tous('.nav__item', nav);
    var modeClic = nav.dataset.ouverture === 'clic';
    var dOuv = parseInt(nav.dataset.delaiOuverture, 10) || 0;
    var dFerm = parseInt(nav.dataset.delaiFermeture, 10) || 0;
    var timer = null;

    function declencheur(li) { return li.querySelector(':scope > a'); }
    function panneau(li) { return li.querySelector(':scope > .mega'); }
    function ouvertes() { return entrees.filter(function (li) { return li.classList.contains(OUVERT); }); }

    function ouvrir(li) {
      if (!panneau(li)) return;
      clearTimeout(timer);
      entrees.forEach(function (autre) { if (autre !== li) fermer(autre); });
      li.classList.add(OUVERT);
      var a = declencheur(li);
      if (a) a.setAttribute('aria-expanded', 'true');
      majBas(header);
    }
    function fermer(li) {
      if (!li.classList.contains(OUVERT)) return;
      li.classList.remove(OUVERT);
      var a = declencheur(li);
      if (a) a.setAttribute('aria-expanded', 'false');
    }
    function toutFermer() {
      clearTimeout(timer);
      entrees.forEach(fermer);
    }
    function planifier(fn, delai) {
      clearTimeout(timer);
      if (delai > 0) timer = setTimeout(fn, delai); else fn();
    }

    entrees.forEach(function (li) {
      var a = declencheur(li);
      var pn = panneau(li);

      li.addEventListener('mouseenter', function () {
        if (etat.pointeur === 'touch' || modeClic) return;
        if (pn) planifier(function () { ouvrir(li); }, ouvertes().length ? 0 : dOuv);
        else planifier(toutFermer, dFerm);
      });
      li.addEventListener('mouseleave', function () {
        if (etat.pointeur === 'touch' || modeClic) return;
        planifier(function () { fermer(li); }, dFerm);
      });

      if (!pn) return;

      /* clic / toucher sur l'entrée : mode « clic » ou premier toucher sur écran tactile */
      a.addEventListener('click', function (e) {
        var tactile = etat.pointeur === 'touch';
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        if (modeClic || (tactile && !li.classList.contains(OUVERT))) {
          e.preventDefault();
          if (li.classList.contains(OUVERT)) fermer(li); else ouvrir(li);
        }
      });

      /* accès clavier : le focus au clavier ouvre le panneau */
      li.addEventListener('focusin', function () {
        if (etat.clavier && !etat.sansOuverture) ouvrir(li);
      });
      li.addEventListener('focusout', function (e) {
        var vers = e.relatedTarget;
        if (vers && li.contains(vers)) return;
        if (etat.clavier || !vers) planifier(function () { if (!li.contains(document.activeElement)) fermer(li); }, 0);
      });

      /* un clic sur un lien du panneau le ferme */
      tous('a', pn).forEach(function (lien) {
        lien.addEventListener('click', function () { fermer(li); });
      });
    });

    /* quitter la barre : tout se referme */
    nav.addEventListener('mouseleave', function () {
      if (etat.pointeur === 'touch' || modeClic) return;
      planifier(toutFermer, dFerm);
    });

    /* clavier */
    nav.addEventListener('keydown', function (e) {
      var cible = e.target;
      var li = cible.closest('.nav__item');
      if (!li) return;
      var estDeclencheur = cible === declencheur(li);
      var pn = panneau(li);

      if (e.key === 'Escape') {
        if (ouvertes().length) {
          e.preventDefault();
          var ouverte = ouvertes()[0];
          toutFermer();
          var d = declencheur(ouverte);
          /* rendre le focus à l'entrée ne doit pas rouvrir le panneau (focusin ouvre au clavier) */
          etat.sansOuverture = true;
          if (d) d.focus();
          etat.sansOuverture = false;
        }
        return;
      }

      if (estDeclencheur) {
        var rang = entrees.indexOf(li);
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var suivant = entrees[(rang + (e.key === 'ArrowRight' ? 1 : -1) + entrees.length) % entrees.length];
          var ds = declencheur(suivant);
          if (ds) ds.focus();
        } else if (e.key === 'ArrowDown' && pn) {
          e.preventDefault();
          ouvrir(li);
          var premier = focusables(pn)[0];
          if (premier) premier.focus();
        } else if ((e.key === ' ' || e.key === 'Spacebar') && pn) {
          e.preventDefault();
          if (li.classList.contains(OUVERT)) fermer(li); else ouvrir(li);
        } else if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault();
          var cibleHF = entrees[e.key === 'Home' ? 0 : entrees.length - 1];
          var dhf = declencheur(cibleHF);
          if (dhf) dhf.focus();
        }
        return;
      }

      /* dans un panneau : flèches haut/bas = lien précédent / suivant, Début / Fin */
      if (pn && pn.contains(cible)) {
        var liste = focusables(pn);
        var i = liste.indexOf(cible);
        if (cible.matches('.mega__continents button') && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault();
          var boutons = tous('.mega__continents button', pn);
          var j = boutons.indexOf(cible);
          var nb = boutons[(j + (e.key === 'ArrowDown' ? 1 : -1) + boutons.length) % boutons.length];
          nb.focus();
          return;
        }
        if (cible.matches('.mega__continents button') && e.key === 'ArrowRight') {
          e.preventDefault();
          var actif = pn.querySelector('.mega__panneau-pays.actif');
          var p1 = actif && focusables(actif)[0];
          if (p1) p1.focus();
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (liste[i + 1]) liste[i + 1].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (i > 0) liste[i - 1].focus(); else { var dd = declencheur(li); if (dd) dd.focus(); }
        } else if (e.key === 'Home') {
          e.preventDefault();
          liste[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          liste[liste.length - 1].focus();
        } else if (e.key === 'ArrowLeft' && !cible.closest('.mega__continents') && pn.querySelector('.mega__continents')) {
          e.preventDefault();
          var cont = pn.querySelector('.mega__continents button.actif') || pn.querySelector('.mega__continents button');
          if (cont) cont.focus();
        }
      }
    });

    /* menu Monde : continents → panneau des pays */
    tous('.mega--monde', nav).forEach(function (mega) {
      function activer(b) {
        tous('.mega__continents button', mega).forEach(function (x) {
          var on = x === b;
          x.classList.toggle('actif', on);
          x.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        tous('.mega__panneau-pays', mega).forEach(function (pn) {
          pn.classList.toggle('actif', pn.dataset.panneau === b.dataset.continent);
        });
      }
      ['pointerover', 'focusin', 'click'].forEach(function (type) {
        mega.addEventListener(type, function (e) {
          var b = e.target.closest('.mega__continents button');
          if (b && (type !== 'pointerover' || etat.pointeur !== 'touch')) activer(b);
        });
      });
    });

    /* mesures */
    window.addEventListener('resize', function () { if (ouvertes().length) majBas(header); });
    window.addEventListener('scroll', function () { if (ouvertes().length) { toutFermer(); majBas(header); } }, { passive: true });
    majBas(header);

    /* éditeur de thème : sélectionner un panneau (ou son encart) dans la barre latérale l'ouvre */
    nav.addEventListener('shopify:block:select', function (e) {
      var li = e.target.closest ? e.target.closest('.nav__item') : null;
      if (li && panneau(li)) { etat.edition = li; ouvrir(li); }
    });
    nav.addEventListener('shopify:block:deselect', function () { etat.edition = null; toutFermer(); });
    header.addEventListener('shopify:block:select', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-menu-principal]') && !etat.edition) {
        var premier = entrees.filter(function (li) { return panneau(li); })[0];
        if (premier) ouvrir(premier);
      }
    });
    header.addEventListener('shopify:block:deselect', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-menu-principal]')) toutFermer();
    });

    nav._entetefermer = toutFermer;
  }

  /* ============================================================
     TIROIR MOBILE
     ============================================================ */
  function initTiroir(racine) {
    tous('[data-tiroir]', racine).forEach(function (tiroir) {
      if (tiroir.dataset.lie) return;
      tiroir.dataset.lie = '1';
      var panneau = tiroir.querySelector('.tiroir__panneau');
      var declencheurs = tous('[data-ouvre-tiroir]');
      var retour = null;

      function ouvrir() {
        retour = document.activeElement;
        tiroir.classList.add(OUVERT);
        tiroir.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('tiroir-ouvert');
        declencheurs.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
        var f = focusables(panneau)[0];
        if (f) setTimeout(function () { f.focus(); }, 60);
      }
      function fermer(rendreFocus) {
        if (!tiroir.classList.contains(OUVERT)) return;
        tiroir.classList.remove(OUVERT);
        tiroir.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('tiroir-ouvert');
        declencheurs.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        if (rendreFocus !== false && retour && retour.focus) retour.focus();
      }

      declencheurs.forEach(function (b) { b.addEventListener('click', ouvrir); });
      tous('[data-ferme-tiroir]', tiroir).forEach(function (b) {
        b.addEventListener('click', function () { fermer(true); });
      });
      tiroir.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); fermer(true); return; }
        if (e.key !== 'Tab') return;
        var liste = focusables(panneau);
        if (!liste.length) return;
        var premier = liste[0], dernier = liste[liste.length - 1];
        if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
        else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
      });
      window.matchMedia(MQ_TIROIR).addEventListener('change', function (m) { if (!m.matches) fermer(false); });

      /* éditeur de thème : sélectionner le bloc ouvre le tiroir */
      declencheurs.forEach(function (b) {
        b.addEventListener('shopify:block:select', function () { ouvrir(); });
        b.addEventListener('shopify:block:deselect', function () { fermer(false); });
      });
    });
  }

  /* ============================================================
     RECHERCHE MOBILE : la loupe déplie le champ
     ============================================================ */
  function initRecherche(racine) {
    tous('[data-recherche-entete]', racine).forEach(function (form) {
      if (form.dataset.lie) return;
      form.dataset.lie = '1';
      var ouvrirBtn = form.querySelector('[data-ouvre-recherche]');
      var fermerBtn = form.querySelector('[data-ferme-recherche]');
      var champ = form.querySelector('input[type="search"]');

      function ouvrir() {
        form.classList.add('ent-rech--ouverte');
        if (ouvrirBtn) ouvrirBtn.setAttribute('aria-expanded', 'true');
        if (champ) setTimeout(function () { champ.focus(); }, 30);
      }
      function fermer(rendreFocus) {
        if (!form.classList.contains('ent-rech--ouverte')) return;
        form.classList.remove('ent-rech--ouverte');
        if (ouvrirBtn) {
          ouvrirBtn.setAttribute('aria-expanded', 'false');
          if (rendreFocus) ouvrirBtn.focus();
        }
      }
      if (ouvrirBtn) ouvrirBtn.addEventListener('click', function () {
        if (form.classList.contains('ent-rech--ouverte')) fermer(true); else ouvrir();
      });
      if (fermerBtn) fermerBtn.addEventListener('click', function () { fermer(true); });
      form.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { fermer(true); }
      });
      form.addEventListener('submit', function (e) {
        /* champ vide : on ne lance pas une recherche pour rien */
        if (champ && !champ.value.trim()) { e.preventDefault(); champ.focus(); }
      });
      document.addEventListener('pointerdown', function (e) {
        if (form.classList.contains('ent-rech--ouverte') && !form.contains(e.target)) fermer(false);
      });
    });
  }

  /* ============================================================
     PANIER : libellé accessible suivant le badge
     ============================================================ */
  function initPanier(racine) {
    tous('.ent-panier', racine).forEach(function (lien) {
      if (lien.dataset.lie) return;
      lien.dataset.lie = '1';
      var bulle = lien.querySelector('.ent-btn__bulle');
      var modele = lien.getAttribute('data-modele-aria');
      if (!bulle || !modele || !window.MutationObserver) return;
      new MutationObserver(function () {
        var n = parseInt(bulle.textContent, 10) || 0;
        lien.setAttribute('aria-label', modele.replace('{n}', n));
      }).observe(bulle, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ============================================================
     initialisation
     ============================================================ */
  function initEntete(racine) {
    tous('[data-entete]', racine).forEach(function (header) { initNav(header); initCompact(header); });
    if (racine.matches && racine.matches('[data-entete]')) { initNav(racine); initCompact(racine); }
    initTiroir(racine);
    initRecherche(racine);
    initPanier(racine);
  }

  function initGlobal() {
    if (etat.global) return;
    etat.global = true;
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Tab' || e.key.indexOf('Arrow') === 0 || e.key === 'Home' || e.key === 'End') etat.clavier = true;
    }, true);
    document.addEventListener('pointerdown', function (e) {
      etat.clavier = false;
      etat.pointeur = e.pointerType || 'mouse';
    }, true);
    document.addEventListener('mousemove', function () {
      if (etat.pointeur === 'touch') etat.pointeur = 'mouse';
    }, { passive: true });
    /* clic en dehors de la barre : on referme les panneaux */
    document.addEventListener('pointerdown', function (e) {
      tous('[data-menu-principal]').forEach(function (nav) {
        if (nav._entetefermer && !nav.contains(e.target)) nav._entetefermer();
      });
    });
  }

  function demarrer(racine) {
    initGlobal();
    initEntete(racine || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { demarrer(document); });
  } else {
    demarrer(document);
  }
  document.addEventListener('shopify:section:load', function (e) { demarrer(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    tous('[data-entete]', e.target).forEach(function (header) { if (header._compactEntete) header._compactEntete(); });
  });
})();
